import { useQuery } from "@tanstack/react-query";
import { useRoute, Link } from "wouter";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/authContext";
import { FollowButton } from "@/components/FollowButton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Loader2, MapPin, Link as LinkIcon, Calendar, Heart, Share2, Grid, Facebook, Youtube, Music2 } from "lucide-react";
import { PostCard } from "@/components/PostCard";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { SchemaMarkup } from "@/components/SchemaMarkup";
import { useSEO } from "@/hooks/useSEO";

export default function PublicProfilePage() {
    const [, params] = useRoute("/u/:username");
    const username = params?.username;
    const { t } = useI18n();
    const { user: currentUser } = useAuth();
    const { toast } = useToast();

    // Fetch Public Profile
    const { data: profile, isLoading, error } = useQuery<any>({
        queryKey: [`/api/profiles/u/${username}`],
        enabled: !!username,
    });

    // Fetch User's Posts
    const { data: posts, isLoading: isLoadingPosts } = useQuery<any[]>({
        queryKey: [`/api/posts?userId=${profile?.id}`],
        enabled: !!profile?.id,
    });

    useSEO({
        title: profile ? `${profile.name || profile.username} (@${profile.username})` : t("account.profile"),
        description: profile?.style_text || t("social.profile.info"),
    });

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (error || !profile) {
        return (
            <div className="container mx-auto px-4 py-12 text-center">
                <h1 className="text-2xl font-bold mb-4">{t("scanner.notFound")}</h1>
                <p className="text-muted-foreground mb-8">{t("social.profile.userNotFound", { username: username || "" })}</p>
                <Link href="/">
                    <Button variant="outline">{t("account.backToHome")}</Button>
                </Link>
            </div>
        );
    }

    const isOwnProfile = currentUser?.id === profile.id;

    const handleShare = () => {
        if (navigator.share) {
            navigator.share({
                title: t("social.profile.shareText", { name: profile.name || profile.username }),
                url: window.location.href,
            });
        } else {
            navigator.clipboard.writeText(window.location.href);
            toast({ title: t("social.profile.linkCopied") });
        }
    };

    const personSchema = {
        "@context": "https://schema.org",
        "@type": "Person",
        "name": profile.name || profile.username,
        "alternateName": profile.username,
        "description": profile.style_text,
        "image": profile.avatar_url,
        "url": `https://vintmap.nl/user/${profile.username}`,
        "address": profile.location ? {
            "@type": "PostalAddress",
            "addressLocality": profile.location
        } : undefined
    };

    return (
        <div className="container mx-auto px-4 py-8 max-w-5xl">
            <SchemaMarkup data={personSchema} />
            {/* Header Section */}
            <div className="flex flex-col md:flex-row gap-8 items-start mb-8">
                <div className="relative">
                    <Avatar className="h-32 w-32 border-4 border-background shadow-xl">
                        <AvatarImage src={profile.avatar_url} alt={profile.username} />
                        <AvatarFallback className="text-3xl bg-secondary text-secondary-foreground">
                            {profile.username?.substring(0, 2).toUpperCase()}
                        </AvatarFallback>
                    </Avatar>
                </div>

                <div className="flex-1 space-y-4 w-full">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-3xl font-bold tracking-tight">
                                {profile.name || profile.username}
                            </h1>
                            <p className="text-muted-foreground flex items-center gap-1">
                                @{profile.username}
                            </p>
                        </div>

                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="icon" onClick={handleShare}>
                                <Share2 className="h-4 w-4" />
                            </Button>
                            {isOwnProfile ? (
                                <Link href="/account">
                                    <Button variant="secondary" size="sm">
                                        {t("social.profile.edit")}
                                    </Button>
                                </Link>
                            ) : (
                                <FollowButton targetId={profile.id} type="user" size="sm" />
                            )}
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        {profile.location && (
                            <div className="flex items-center gap-1">
                                <MapPin className="h-4 w-4" /> {profile.location}
                            </div>
                        )}
                        <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {t("social.profile.since", { date: new Date(profile.created_at).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) })}
                        </div>
                        {profile.social_links?.website && (
                            <a
                                href={profile.social_links.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 hover:text-primary transition-colors"
                            >
                                <LinkIcon className="h-4 w-4" /> {t("social.profile.website")}
                            </a>
                        )}
                        {profile.social_links?.facebook && (
                            <a
                                href={profile.social_links.facebook}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 hover:text-primary transition-colors"
                            >
                                <Facebook className="h-4 w-4" /> Facebook
                            </a>
                        )}
                        {profile.social_links?.youtube && (
                            <a
                                href={profile.social_links.youtube}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 hover:text-primary transition-colors"
                            >
                                <Youtube className="h-4 w-4" /> YouTube
                            </a>
                        )}
                        {profile.social_links?.tiktok && (
                            <a
                                href={profile.social_links.tiktok}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 hover:text-primary transition-colors"
                            >
                                <Music2 className="h-4 w-4" /> TikTok
                            </a>
                        )}
                    </div>

                    <div className="flex gap-6 pt-2">
                        <div className="flex flex-col">
                            <span className="text-lg font-bold">{profile.followers_count || 0}</span>
                            <span className="text-xs text-muted-foreground uppercase tracking-wider">{t("social.followers")}</span>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-lg font-bold">{profile.following_count || 0}</span>
                            <span className="text-xs text-muted-foreground uppercase tracking-wider">{t("social.following")}</span>
                        </div>
                        <div className="flex flex-col">
                            <span className="text-lg font-bold">{(posts || []).filter((p: any) => !p.as_shop_id).length}</span>
                            <span className="text-xs text-muted-foreground uppercase tracking-wider">{t("account.myFinds")}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content Tabs */}
            <Tabs defaultValue="posts" className="space-y-6">
                <TabsList className="w-full justify-start border-b rounded-none h-auto p-0 bg-transparent space-x-6">
                    <TabsTrigger
                        value="posts"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-2 py-3"
                    >
                        <Grid className="h-4 w-4 mr-2" />
                        {t("account.myFinds")}
                    </TabsTrigger>
                    <TabsTrigger
                        value="about"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-2 py-3"
                    >
                        {t("social.profile.info")}
                    </TabsTrigger>
                    {profile.is_wishlist_public && (
                        <TabsTrigger
                            value="wishlist"
                            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-2 py-3"
                        >
                            <Heart className="h-4 w-4 mr-2" />
                            {t("social.profile.wishlist")}
                        </TabsTrigger>
                    )}
                </TabsList>

                <TabsContent value="posts" className="pt-2">
                    {isLoadingPosts ? (
                        <div className="flex justify-center p-12">
                            <Loader2 className="h-6 w-6 animate-spin" />
                        </div>
                    ) : !posts || posts.length === 0 ? (
                        <Card className="border-dashed">
                            <CardContent className="flex flex-col items-center justify-center p-12 text-center">
                                <Grid className="h-12 w-12 text-muted-foreground mb-4 opacity-20" />
                                <p className="text-muted-foreground">{t("social.profile.noPosts")}</p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {posts.filter((p: any) => !p.as_shop_id).map((post: any) => (
                                <PostCard key={post.id} post={post} />
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="about" className="pt-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">{t("social.style.bio")}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="whitespace-pre-wrap text-muted-foreground italic">
                                    {profile.style_text || t("social.profile.noStyle")}
                                </p>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">{t("social.style.lookingFor")}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="whitespace-pre-wrap text-muted-foreground">
                                    {profile.looking_for_text || t("social.profile.noInterests")}
                                </p>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {profile.is_wishlist_public && (
                    <TabsContent value="wishlist">
                        <div className="py-8 text-center text-muted-foreground">
                            {t("social.profile.wishlistComingSoon")}
                        </div>
                    </TabsContent>
                )}
            </Tabs>
        </div>
    );
}
